import { toRuleWriteDto } from './ruleDraft';

export const RULE_EXPORT_FORMAT = 'kea.merchandising.rules-export.v1';
export const MAX_IMPORT_BYTES = 1024 * 1024;
export const MAX_PORTABLE_RULES = 100;
export const MAX_JSON_DEPTH = 32;

const IDENTITY_FIELDS = new Set([
  'id', 'schema_version', 'revision', 'status', 'source', 'audit',
  'runtime_effect', 'created_at', 'updated_at', 'origin', 'index_state',
]);

const jsonByteLength = (text) => {
  if (typeof Blob !== 'undefined') return new Blob([text]).size;
  return unescape(encodeURIComponent(text)).length;
};

// A bounded JSON scanner detects duplicate object keys and excessive nesting
// before JSON.parse discards that security-relevant information.
const inspectJson = (text) => {
  let offset = 0;
  const skip = () => { while (/\s/.test(text[offset] || '')) offset += 1; };
  const string = () => {
    if (text[offset] !== '"') throw new Error('INVALID_JSON');
    const start = offset;
    offset += 1;
    while (offset < text.length) {
      if (text[offset] === '\\') offset += 2;
      else if (text[offset] === '"') {
        offset += 1;
        return JSON.parse(text.slice(start, offset));
      } else offset += 1;
    }
    throw new Error('INVALID_JSON');
  };
  const primitive = () => {
    const start = offset;
    while (offset < text.length && !/[\s,\]}]/.test(text[offset])) offset += 1;
    JSON.parse(text.slice(start, offset));
  };
  const value = (depth) => {
    if (depth > MAX_JSON_DEPTH) throw new Error('JSON_DEPTH_EXCEEDED');
    skip();
    if (text[offset] === '{') {
      offset += 1;
      const keys = new Set();
      skip();
      if (text[offset] === '}') { offset += 1; return; }
      while (offset < text.length) {
        skip();
        const key = string();
        if (keys.has(key)) throw new Error(`DUPLICATE_KEY:${key}`);
        keys.add(key);
        skip();
        if (text[offset] !== ':') throw new Error('INVALID_JSON');
        offset += 1;
        value(depth + 1);
        skip();
        if (text[offset] === '}') { offset += 1; return; }
        if (text[offset] !== ',') throw new Error('INVALID_JSON');
        offset += 1;
      }
      throw new Error('INVALID_JSON');
    }
    if (text[offset] === '[') {
      offset += 1;
      skip();
      if (text[offset] === ']') { offset += 1; return; }
      while (offset < text.length) {
        value(depth + 1);
        skip();
        if (text[offset] === ']') { offset += 1; return; }
        if (text[offset] !== ',') throw new Error('INVALID_JSON');
        offset += 1;
      }
      throw new Error('INVALID_JSON');
    }
    if (text[offset] === '"') string();
    else primitive();
  };
  value(1);
  skip();
  if (offset !== text.length) throw new Error('INVALID_JSON');
};

export const portableRule = (rule) => {
  if (!rule || rule.kind === 'category_priority') {
    throw new Error('CATEGORY_PRIORITY_NOT_PORTABLE');
  }
  return toRuleWriteDto(rule);
};

const sortDeep = (value) => {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sortDeep(value[key])]));
  }
  return value;
};

export const createRuleExportManifest = ({ rules, failures = [] }) => {
  if (rules.length > MAX_PORTABLE_RULES) throw new Error('RULE_LIMIT_EXCEEDED');
  return {
    format: RULE_EXPORT_FORMAT,
    exported_at: new Date().toISOString(),
    complete: failures.length === 0,
    rules: rules.map(portableRule),
    failures: failures.map(({ id, code }) => ({ id: String(id), code })),
  };
};

export const serializeRuleExportManifest = (manifest) =>
  `${JSON.stringify(sortDeep(manifest), null, 2)}\n`;

export const parseRuleImport = (text) => {
  if (typeof text !== 'string' || jsonByteLength(text) > MAX_IMPORT_BYTES) {
    throw new Error('IMPORT_SIZE_EXCEEDED');
  }
  inspectJson(text);
  const manifest = JSON.parse(text);
  if (!manifest || manifest.format !== RULE_EXPORT_FORMAT || !Array.isArray(manifest.rules)) {
    throw new Error('INVALID_IMPORT_FORMAT');
  }
  if (manifest.rules.length > MAX_PORTABLE_RULES) throw new Error('RULE_LIMIT_EXCEEDED');
  const allowedManifestKeys = new Set(['format', 'exported_at', 'complete', 'rules', 'failures']);
  Object.keys(manifest).forEach((key) => {
    if (!allowedManifestKeys.has(key)) throw new Error(`UNKNOWN_MANIFEST_FIELD:${key}`);
  });
  manifest.rules.forEach((rule, index) => {
    if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
      throw new Error(`INVALID_RULE:${index}`);
    }
    Object.keys(rule).forEach((key) => {
      if (IDENTITY_FIELDS.has(key)) throw new Error(`IDENTITY_FIELD:${index}:${key}`);
    });
    const legal = (rule.phase === 'index' && rule.kind === 'product_ranking')
      || (rule.phase === 'query' && rule.kind === 'query_override');
    if (!legal) throw new Error(`ILLEGAL_PHASE_KIND:${index}`);
  });
  return manifest.rules;
};

export async function runWithConcurrency(items, worker, {
  concurrency = 3,
  isCancelled = () => false,
  onUpdate = () => {},
} = {}) {
  if (!Number.isInteger(concurrency) || concurrency < 1) throw new TypeError('Invalid concurrency.');
  const results = Array(items.length).fill(null);
  let cursor = 0;
  const runner = async () => {
    while (cursor < items.length) {
      if (isCancelled()) return;
      const index = cursor;
      cursor += 1;
      onUpdate(index, 'running');
      try {
        results[index] = { status: 'succeeded', value: await worker(items[index], index) };
      } catch (error) {
        results[index] = { status: 'failed', error };
      }
      onUpdate(index, results[index].status, results[index]);
    }
  };
  await Promise.all(Array.from(
    { length: Math.min(concurrency, items.length) },
    () => runner(),
  ));
  for (let index = 0; index < results.length; index += 1) {
    if (!results[index]) results[index] = { status: 'not-started' };
  }
  return results;
}
